/**
 * Seslendirme (v0.1.255): GET /voice every time the screen opens and every 20 seconds while a job is open, POST
 * /voice/jobs from the form. The last view stays on screen while it loads.
 */

import type { VoiceJobBody, VoiceView } from '@twin/contracts';
import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import { useStore } from './context.js';

export interface VoiceState {
  view: VoiceView | null;
  error: string | null;
  loading: boolean;
  sending: boolean;
  sendError: string | null;
}

export class VoiceStore {
  private state: VoiceState = { view: null, error: null, loading: false, sending: false, sendError: null };
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): VoiceState => this.state;

  private set(patch: Partial<VoiceState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Reads again, keeping the current view meanwhile; one read at a time. */
  reload(): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    this.set({ loading: true });
    const run = (async () => {
      try {
        this.set({ view: await this.api.getVoice(), error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** Writes a job, then reads the list again; true when the job was written. */
  async submit(body: VoiceJobBody): Promise<boolean> {
    if (this.state.sending) return false;
    this.set({ sending: true, sendError: null });
    try {
      await this.api.createVoiceJob(body);
      this.set({ sending: false });
      await this.reload();
      return true;
    } catch (error) {
      this.set({ sending: false, sendError: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }
}

export function useVoice(): { store: VoiceStore; state: VoiceState } {
  const store = useStore().voice;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
